import type { AuthenticatedSession } from './authMethods';

import { ACCOUNT_SLOT } from '../../util/multiaccount';
import { refreshSession } from './authMethods';

const LEGACY_STORAGE_KEY = 'tgim_backend_session';
const ACCESS_TOKEN_SKEW_MS = 60_000;

function storageKey(): string {
  return `${LEGACY_STORAGE_KEY}_${ACCOUNT_SLOT || 1}`;
}

export type StoredTgimSession = {
  openImUserId: string;
  openImToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: string;
};

export type StoreTgimSessionOptions = {
  /**
   * Mirrors Android/iOS: keep-signed-in means persist `refresh_token` in localStorage.
   * When false (Web 「保持登录」unchecked), session lives in sessionStorage only — close tab = logout.
   */
  rememberMe?: boolean;
};

function writeSessionJson(raw: string, persist: boolean): void {
  if (persist) {
    localStorage.setItem(storageKey(), raw);
    sessionStorage.removeItem(storageKey());
  } else {
    sessionStorage.setItem(storageKey(), raw);
    localStorage.removeItem(storageKey());
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  }
}

export function storeTgimSession(session: AuthenticatedSession, options?: StoreTgimSessionOptions): void {
  const rememberMe = options?.rememberMe !== false;
  // Refresh responses sometimes omit `refresh_token`; never wipe a still-valid stored one when persisting.
  const previous = loadTgimSession();
  const stored: StoredTgimSession = {
    openImUserId: session.openim_user_id,
    openImToken: session.openim_token,
    accessToken: session.access_token,
    refreshToken: rememberMe
      ? (session.refresh_token ?? previous?.refreshToken)
      : undefined,
    expiresAt: session.expires_at,
  };
  writeSessionJson(JSON.stringify(stored), rememberMe);
  // Drop legacy unscoped key so slot-2 cannot accidentally resume slot-1.
  if (ACCOUNT_SLOT && ACCOUNT_SLOT !== 1) {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  }
}

export function loadTgimSession(): StoredTgimSession | undefined {
  const key = storageKey();
  let raw = sessionStorage.getItem(key) || localStorage.getItem(key);
  // Migrate slot-1 from the pre-isolation key once (localStorage only).
  if (!raw && (ACCOUNT_SLOT || 1) === 1) {
    raw = localStorage.getItem(LEGACY_STORAGE_KEY) || null;
    if (raw) {
      localStorage.setItem(key, raw);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  }
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as StoredTgimSession;
  } catch {
    return undefined;
  }
}

export function clearTgimSession(): void {
  const key = storageKey();
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
  if ((ACCOUNT_SLOT || 1) === 1) {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  }
}

function isAccessTokenFresh(session: StoredTgimSession): boolean {
  const expiresAt = new Date(session.expiresAt).getTime();
  // Refresh a minute early so in-flight API calls don't race a hard expiry.
  return Number.isFinite(expiresAt) && expiresAt - ACCESS_TOKEN_SKEW_MS > Date.now();
}

// Whether a persisted session exists and its access token hasn't already expired.
export function hasValidTgimSession(): boolean {
  const session = loadTgimSession();
  if (!session) return false;
  return isAccessTokenFresh(session);
}

/**
 * True when a page reload should attempt to resume into the app — either the access token is
 * still fresh, or a refresh_token is available to rotate credentials (Android/iOS behaviour).
 */
export function hasResumableTgimSession(): boolean {
  const session = loadTgimSession();
  if (!session?.accessToken || !session.openImToken || !session.openImUserId) return false;
  return isAccessTokenFresh(session) || Boolean(session.refreshToken);
}

// Dedupes concurrent `ensureTgimSession` refreshes (e.g. two tabs, or several backend calls firing
// right after expiry) so they share one `/auth/refresh` instead of racing with the same
// not-yet-rotated `refreshToken` — the loser of that race would otherwise get logged out even
// though the winner's refresh succeeded moments earlier.
let inFlightRefresh: Promise<StoredTgimSession | undefined> | undefined;

async function refreshTgimSession(session: StoredTgimSession): Promise<StoredTgimSession | undefined> {
  if (!session.refreshToken) {
    clearTgimSession();
    return undefined;
  }

  try {
    const next = await refreshSession(session.refreshToken);
    // Keep whatever storage the previous session used (sessionStorage vs localStorage).
    const persist = Boolean(localStorage.getItem(storageKey()));
    storeTgimSession(next, { rememberMe: persist });
    return loadTgimSession();
  } catch {
    clearTgimSession();
    return undefined;
  }
}

/**
 * Ensures localStorage/sessionStorage holds a fresh access + OpenIM token. Refreshes via
 * `/auth/refresh` when access has expired (or is within a short skew window).
 */
export async function ensureTgimSession(options?: { forceRefresh?: boolean }): Promise<StoredTgimSession | undefined> {
  if (inFlightRefresh) return inFlightRefresh;

  const session = loadTgimSession();
  if (!session) return undefined;

  const needsRefresh = options?.forceRefresh || !isAccessTokenFresh(session);
  if (!needsRefresh) return session;

  inFlightRefresh = refreshTgimSession(session).finally(() => {
    inFlightRefresh = undefined;
  });
  return inFlightRefresh;
}

/** Prefer this over raw `loadTgimSession().accessToken` so mid-session JWT expiry self-heals. */
export async function getTgimAccessToken(): Promise<string | undefined> {
  const session = await ensureTgimSession();
  return session?.accessToken;
}
